/**
 * Node commands.
 *
 * Each handler is a pair: `validate` decides whether the edit is legal, `execute` performs it and
 * returns the inverse. Keeping them separate matters for the UI — a drag gesture asks `validate`
 * on every frame to decide whether to show a valid drop target, and it must be able to ask
 * without changing anything.
 */
import { canReparent, createNode, requireNode, subtree, toPlainText, } from '@orbit-mind/domain';
import { appError, err, ok } from '@orbit-mind/shared-types';
import { command, requireEditable, requireParentId, requirePresent, setOrDrop, touch, withNode, withoutNodes, } from './shared';
// ---------------------------------------------------------------------------------------------
// create
// ---------------------------------------------------------------------------------------------
export function validateCreateNode(doc, payload) {
    if (doc.nodes[payload.nodeId] !== undefined) {
        return err(appError('VALIDATION_FAILED', `node already exists: ${payload.nodeId}`));
    }
    const parent = requirePresent(doc, payload.parentId, 'PARENT_NOT_FOUND');
    if (!parent.ok)
        return parent;
    return requireEditable(parent.value);
}
export function executeCreateNode(doc, payload, meta) {
    const node = createNode({
        id: payload.nodeId,
        mapId: doc.map.id,
        parentId: payload.parentId,
        order: payload.order,
        now: meta.timestamp,
        by: meta.authorId,
        ...(payload.text !== undefined ? { text: payload.text } : {}),
        ...(payload.richText !== undefined ? { richText: payload.richText } : {}),
        ...(payload.side !== undefined ? { side: payload.side } : {}),
        ...(payload.type !== undefined ? { type: payload.type } : {}),
        ...(payload.position !== undefined ? { position: payload.position } : {}),
    });
    return {
        document: withNode(doc, node, meta),
        inverse: command('delete-node', { nodeId: payload.nodeId }, meta),
    };
}
// ---------------------------------------------------------------------------------------------
// delete and restore
// ---------------------------------------------------------------------------------------------
export function validateDeleteNode(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    if (node.value.parentId === null) {
        return err(appError('ROOT_IMMUTABLE', 'the root node cannot be deleted'));
    }
    return requireEditable(node.value);
}
/**
 * Deletes a node and everything below it.
 *
 * The inverse captures the entire subtree plus every edge touching it, because after this runs
 * nothing else remembers them. That capture is what makes deletion undoable without snapshotting
 * the whole document — the specification's "commands and patches, not a snapshot per action".
 */
export function executeDeleteNode(doc, payload, meta) {
    const removed = subtree(doc, payload.nodeId);
    const removedIds = new Set(removed.map((node) => node.id));
    const orphanedEdges = Object.values(doc.edges).filter((edge) => removedIds.has(edge.sourceNodeId) || removedIds.has(edge.targetNodeId));
    return {
        document: withoutNodes(doc, removedIds, meta),
        inverse: command('restore-nodes', { nodes: removed, edges: orphanedEdges }, meta),
    };
}
export function validateRestoreNodes(doc, payload) {
    for (const node of payload.nodes) {
        if (doc.nodes[node.id] !== undefined) {
            return err(appError('VALIDATION_FAILED', `node already exists: ${node.id}`));
        }
    }
    // The subtree's own root must have somewhere to reattach to. Its descendants reattach to each
    // other, so only that one parent has to still be there.
    const first = payload.nodes[0];
    if (first?.parentId != null && doc.nodes[first.parentId] === undefined) {
        return err(appError('PARENT_NOT_FOUND', `parent no longer exists: ${first.parentId}`));
    }
    return ok(undefined);
}
export function executeRestoreNodes(doc, payload, meta) {
    const nodes = { ...doc.nodes };
    for (const node of payload.nodes)
        nodes[node.id] = node;
    const edges = { ...doc.edges };
    for (const edge of payload.edges)
        edges[edge.id] = edge;
    const first = payload.nodes[0];
    return {
        document: { map: touch(doc.map, meta), nodes, edges },
        // Restoring a subtree is undone by deleting its root again, which takes the rest with it.
        inverse: first === undefined
            ? command('restore-nodes', { nodes: [], edges: [] }, meta)
            : command('delete-node', { nodeId: first.id }, meta),
    };
}
// ---------------------------------------------------------------------------------------------
// geometry and structure
// ---------------------------------------------------------------------------------------------
export function validateMoveNode(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return requireEditable(node.value);
}
export function executeMoveNode(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    const moved = setOrDrop({
        ...node,
        x: payload.position.x,
        y: payload.position.y,
        updatedAt: meta.timestamp,
        updatedBy: meta.authorId,
    }, 'pinned', 
    // `false` and absent mean the same thing, so only one of them is ever stored. Otherwise
    // undoing a pin would leave `pinned: false` where the field had not existed.
    payload.pinned === true ? true : undefined);
    return {
        document: withNode(doc, moved, meta),
        inverse: command('move-node', {
            nodeId: payload.nodeId,
            position: { x: node.x, y: node.y },
            ...(node.pinned === true ? { pinned: true } : {}),
        }, meta),
    };
}
export function validateReparentNode(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    const editable = requireEditable(node.value);
    if (!editable.ok)
        return editable;
    if (doc.nodes[payload.newParentId] === undefined) {
        return err(appError('PARENT_NOT_FOUND', `parent not found: ${payload.newParentId}`));
    }
    // The cycle check lives in the domain, so the gesture, the command and the integrity audit all
    // ask the same question and cannot disagree about the answer.
    if (!canReparent(doc, payload.nodeId, payload.newParentId)) {
        return err(appError('CYCLE_DETECTED', `cannot move ${payload.nodeId} into ${payload.newParentId}`));
    }
    return ok(undefined);
}
export function executeReparentNode(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    const moved = {
        ...node,
        parentId: payload.newParentId,
        order: payload.order,
        updatedAt: meta.timestamp,
        updatedBy: meta.authorId,
    };
    return {
        document: withNode(doc, moved, meta),
        inverse: command('reparent-node', {
            nodeId: payload.nodeId,
            // The root is the only node with a null parent, and it can never be reparented, so
            // `node.parentId` is non-null for anything that reached here.
            newParentId: requireParentId(node),
            order: node.order,
        }, meta),
    };
}
export function validateReorderNode(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return requireEditable(node.value);
}
export function executeReorderNode(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    return {
        document: withNode(doc, {
            ...node,
            order: payload.order,
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        }, meta),
        inverse: command('reorder-node', { nodeId: payload.nodeId, order: node.order }, meta),
    };
}
export function validateChangeNodeSide(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return requireEditable(node.value);
}
/**
 * Moves one node to the other side.
 *
 * Only this node: the whole-branch operation is planned by the domain's `planSideChange` and
 * dispatched as a batch of these, so the branch rule lives in one place and the command stays a
 * single field change that inverts trivially.
 */
export function executeChangeNodeSide(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    return {
        document: withNode(doc, {
            ...node,
            side: payload.side,
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        }, meta),
        inverse: command('change-node-side', { nodeId: payload.nodeId, side: node.side }, meta),
    };
}
// ---------------------------------------------------------------------------------------------
// content
// ---------------------------------------------------------------------------------------------
/**
 * The longest an emoji or an icon name may be.
 *
 * Sixteen, not one: a single emoji can be four code points joined by zero-width joiners — a
 * family, a flag, a profession — and a limit of one would refuse most of the ones people reach
 * for. Sixteen is far more than any of those and far less than a paragraph pasted into the wrong
 * field.
 */
export const MAX_GLYPH_LENGTH = 16;
export function validateUpdateNode(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    const editable = requireEditable(node.value);
    if (!editable.ok)
        return editable;
    const progress = payload.changes.progress;
    if (progress !== undefined && (!Number.isFinite(progress) || progress < 0 || progress > 100)) {
        return err(appError('VALIDATION_FAILED', 'progress must be between 0 and 100'));
    }
    const dueDate = payload.changes.dueDate;
    if (dueDate !== undefined && (!Number.isFinite(dueDate) || dueDate < 0)) {
        return err(appError('VALIDATION_FAILED', 'due date must be a valid timestamp'));
    }
    for (const key of ['emoji', 'icon']) {
        const value = payload.changes[key];
        if (value !== undefined && value.length > MAX_GLYPH_LENGTH) {
            return err(appError('VALIDATION_FAILED', `${key} may be at most ${String(MAX_GLYPH_LENGTH)} characters`));
        }
    }
    for (const key of ['width', 'height']) {
        const value = payload.changes[key];
        // A node with no area cannot be clicked, hit-tested or exported, and a negative one draws
        // inside out. Refused at the command rather than clamped, because a size nobody asked for is
        // worse than an edit that says why it was not made.
        if (value !== undefined && !(Number.isFinite(value) && value >= 0)) {
            return err(appError('VALIDATION_FAILED', `${key} must be a non-negative number`));
        }
    }
    return ok(undefined);
}
export function executeUpdateNode(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    let updated = { ...node, ...payload.changes };
    for (const key of payload.clear ?? [])
        updated = setOrDrop(updated, key, undefined);
    /*
     * The inverse restores exactly the keys this touched, and restores their *absence* where they
     * had none. A key that was missing and is now set has to go back to missing — writing
     * `{ emoji: undefined }` into `changes` would set the key to undefined rather than remove it,
     * and undo would leave the node holding an emoji-shaped hole.
     */
    const previous = {};
    const restore = [];
    const touched = new Set([
        ...Object.keys(payload.changes),
        ...(payload.clear ?? []),
    ]);
    for (const key of touched) {
        const before = node[key];
        if (before === undefined)
            restore.push(key);
        else
            previous[key] = before;
    }
    return {
        document: withNode(doc, { ...updated, updatedAt: meta.timestamp, updatedBy: meta.authorId }, meta),
        inverse: command('update-node', {
            nodeId: payload.nodeId,
            changes: previous,
            ...(restore.length === 0 ? {} : { clear: restore }),
        }, meta),
    };
}
export function validateUpdateNodeText(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return requireEditable(node.value);
}
export function executeUpdateNodeText(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    const plainText = toPlainText(payload.richText);
    return {
        document: withNode(doc, {
            ...node,
            richText: payload.richText,
            // Derived here rather than taken from the payload, so the two cannot be sent out of step.
            plainText,
            title: plainText.split('\n')[0] ?? '',
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        }, meta),
        inverse: command('update-node-text', { nodeId: payload.nodeId, richText: node.richText }, meta),
    };
}
export function validateUpdateNodeStyle(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return requireEditable(node.value);
}
export function executeUpdateNodeStyle(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    return {
        document: withNode(doc, {
            ...node,
            // `replace` is what the inverse uses. Merging the previous style back over the new one
            // cannot remove a property the edit introduced, so undo would leave it behind.
            style: payload.replace === true ? payload.style : { ...node.style, ...payload.style },
            ...(payload.shape !== undefined ? { shape: payload.shape } : {}),
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        }, meta),
        inverse: command('update-node-style', { nodeId: payload.nodeId, style: node.style, shape: node.shape, replace: true }, meta),
    };
}
export function validateSetCollapsed(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    return node.ok ? ok(undefined) : node;
}
/**
 * Collapses or expands a node.
 *
 * Deliberately does not require the node to be editable: collapsing is a view preference, and a
 * locked branch that cannot be folded away is an annoyance with no protective value.
 */
export function executeSetCollapsed(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    return {
        document: withNode(doc, {
            ...node,
            collapsed: payload.collapsed,
            updatedAt: meta.timestamp,
            updatedBy: meta.authorId,
        }, meta),
        inverse: command('set-collapsed', { nodeId: payload.nodeId, collapsed: node.collapsed }, meta),
    };
}
// ---------------------------------------------------------------------------------------------
// attachments
// ---------------------------------------------------------------------------------------------
export function validateAttachment(doc, payload) {
    const node = requirePresent(doc, payload.nodeId);
    if (!node.ok)
        return node;
    return requireEditable(node.value);
}
export function executeAddAttachment(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    const existing = node.attachments ?? [];
    const id = payload.attachmentId;
    // Attaching the same file twice is a no-op rather than a duplicate row, because the second
    // attempt is almost always a double-tap.
    const attachments = existing.includes(id) ? existing : [...existing, id];
    return {
        document: withNode(doc, setOrDrop({ ...node, updatedAt: meta.timestamp, updatedBy: meta.authorId }, 'attachments', attachments.length > 0 ? attachments : undefined), meta),
        inverse: command('remove-attachment', { nodeId: payload.nodeId, attachmentId: id }, meta),
    };
}
export function executeRemoveAttachment(doc, payload, meta) {
    const node = requireNode(doc, payload.nodeId);
    const existing = node.attachments ?? [];
    const id = payload.attachmentId;
    const remaining = existing.filter((value) => value !== id);
    return {
        document: withNode(doc, setOrDrop({ ...node, updatedAt: meta.timestamp, updatedBy: meta.authorId }, 'attachments', 
        // An empty list is stored as absence, so removing the last attachment returns the node
        // to the shape it had before the first one was added.
        remaining.length > 0 ? remaining : undefined), meta),
        inverse: command('add-attachment', { nodeId: payload.nodeId, attachmentId: id }, meta),
    };
}
//# sourceMappingURL=node.js.map